import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  User, Camera, CreditCard, Car, FileText,
  Upload, CheckCircle, XCircle, ArrowLeft, Loader2,
  Eye
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const documentTypes = [
  {
    key: "profile_photo",
    label: "Photo de profil",
    description: "Votre photo d'identité",
    icon: User,
    color: "from-blue-500 to-blue-600"
  },
  {
    key: "id_front",
    label: "Pièce d'identité (Recto)",
    description: "CNI ou Passeport - Face avant",
    icon: CreditCard,
    color: "from-green-500 to-green-600"
  },
  {
    key: "id_back",
    label: "Pièce d'identité (Verso)",
    description: "CNI ou Passeport - Face arrière",
    icon: CreditCard,
    color: "from-green-500 to-green-600"
  },
  {
    key: "carte_grise_front",
    label: "Carte grise (Recto)",
    description: "Carte grise du véhicule - Face avant",
    icon: Car,
    color: "from-orange-500 to-orange-600"
  },
  {
    key: "carte_grise_back",
    label: "Carte grise (Verso)",
    description: "Carte grise du véhicule - Face arrière",
    icon: Car,
    color: "from-orange-500 to-orange-600"
  },
  {
    key: "permis_front",
    label: "Permis de conduire (Recto)",
    description: "Permis de conduire - Face avant",
    icon: FileText,
    color: "from-purple-500 to-purple-600"
  },
  {
    key: "permis_back",
    label: "Permis de conduire (Verso)",
    description: "Permis de conduire - Face arrière",
    icon: FileText,
    color: "from-purple-500 to-purple-600"
  }
];

export default function CourierProfilePage({ user, onLogout }) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/courier-documents/${user.id}`);
      setDocuments(response.data.documents || {});
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (documentType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Format non supporté. Utilisez JPG, PNG ou WebP.");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5MB)");
      return;
    }

    setUploadingType(documentType);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("courier_id", user.id);
    formData.append("document_type", documentType);

    try {
      const response = await axios.post(`${API}/courier-documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setDocuments(prev => ({
          ...prev,
          [documentType]: response.data.document_url
        }));
        toast.success("Document téléchargé avec succès!");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du téléchargement");
    } finally {
      setUploadingType(null);
    }
  };

  const handleDeleteDocument = async (documentType) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce document?")) return;

    try {
      await axios.delete(`${API}/courier-documents/${user.id}/${documentType}`);
      setDocuments(prev => {
        const newDocs = { ...prev };
        delete newDocs[documentType];
        return newDocs;
      });
      toast.success("Document supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const triggerFileInput = (documentType) => {
    fileInputRefs.current[documentType]?.click();
  };

  const completedCount = documentTypes.filter(dt => documents[dt.key]).length;
  const isComplete = completedCount === documentTypes.length;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <nav className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 md:px-8 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/courier')}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
                Mon Profil Livreur
              </h1>
              <p className="text-xs text-green-100">{user.full_name}</p>
            </div>
          </div>
          <Button onClick={onLogout} variant="outline" className="rounded-full text-white border-white hover:bg-white hover:text-green-700">
            Déconnexion
          </Button>
        </div>
      </nav>

      <div className="px-4 md:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Card */}
          <Card className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-green-800">Documents requis</h2>
                  <p className="text-sm text-green-600">
                    {completedCount}/{documentTypes.length} documents téléchargés
                  </p>
                </div>
                {isComplete ? (
                  <div className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Profil complet</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-full">
                    <Loader2 className="w-5 h-5" />
                    <span className="font-semibold">En cours</span>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              <div className="w-full bg-green-200 rounded-full h-3">
                <div
                  className="bg-green-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(completedCount / documentTypes.length) * 100}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Documents Grid */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {documentTypes.map((docType) => {
                const IconComponent = docType.icon;
                const isUploaded = !!documents[docType.key];
                const isUploading = uploadingType === docType.key;

                return (
                  <Card
                    key={docType.key}
                    className={`overflow-hidden transition-all duration-300 ${
                      isUploaded ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-green-300'
                    }`}
                    data-testid={`document-card-${docType.key}`}
                  >
                    <CardHeader className={`bg-gradient-to-r ${docType.color} text-white py-3`}>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <IconComponent className="w-5 h-5" />
                        {docType.label}
                        {isUploaded && <CheckCircle className="w-4 h-4 ml-auto" />}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground mb-4">{docType.description}</p>

                      {/* Hidden file input */}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        ref={el => fileInputRefs.current[docType.key] = el}
                        onChange={(e) => handleFileSelect(docType.key, e)}
                        data-testid={`file-input-${docType.key}`}
                      />

                      {isUploaded ? (
                        <div className="space-y-3">
                          {/* Preview */}
                          <div
                            className="relative w-full h-40 bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
                            onClick={() => setPreviewImage(BACKEND_URL + documents[docType.key])}
                          >
                            <img
                              src={BACKEND_URL + documents[docType.key]}
                              alt={docType.label}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Eye className="w-8 h-8 text-white" />
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button
                              onClick={() => triggerFileInput(docType.key)}
                              variant="outline"
                              className="flex-1 rounded-full"
                              disabled={isUploading}
                            >
                              <Camera className="w-4 h-4 mr-2" />
                              Remplacer
                            </Button>
                            <Button
                              onClick={() => handleDeleteDocument(docType.key)}
                              variant="outline"
                              className="text-red-600 hover:bg-red-50 rounded-full"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => triggerFileInput(docType.key)}
                          className={`w-full rounded-full bg-gradient-to-r ${docType.color} hover:opacity-90`}
                          disabled={isUploading}
                          data-testid={`upload-btn-${docType.key}`}
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Téléchargement...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Télécharger
                            </>
                          )}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Info Card */}
          <Card className="mt-8 bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <h3 className="font-bold text-blue-800 mb-2">Pourquoi ces documents?</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• La <strong>photo de profil</strong> permet aux clients de vous identifier</li>
                <li>• La <strong>pièce d'identité</strong> vérifie votre identité pour la sécurité</li>
                <li>• La <strong>carte grise</strong> confirme que vous êtes propriétaire du véhicule</li>
                <li>• Le <strong>permis de conduire</strong> prouve que vous êtes autorisé à conduire</li>
              </ul>
              <p className="text-xs text-blue-600 mt-4">
                Tous les documents sont stockés de manière sécurisée et ne seront utilisés que pour la vérification de votre compte.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <Button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 rounded-full"
            >
              <XCircle className="w-6 h-6" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
